/*
 * Fall for the Floorplan quiz.
 *
 * Two surfaces share this file:
 *
 * 1. The quiz page (/floorplan-finder/preferences/, pages/quiz.html): one
 *    full-viewport slide per question, then a "Calculating Your Matches..."
 *    gate that requires login/signup through the retail user modal
 *    (user/retail_user_modals.html). Once authenticated, the answers post to
 *    /xhr/quiz-match/ (weighted scoring per the client's spreadsheet -- see
 *    utils/quiz_matching.py) and the visitor is redirected to their best
 *    match's quiz page.
 *
 * 2. The match decision pages (/floorplans/quiz/<plan>/, MasterPlanQuizView):
 *    the top matches present one at a time. "Love It!" favorites the plan
 *    (same as the heart button); either choice advances to the next match.
 */

const MATCH_STORE_KEY = "ff-quiz-matches"

const authenticated = function () {
    return window.oi && oi.loaded && oi.loaded.user && oi.loaded.user.authenticated
}

const readMatchStore = function () {
    try {
        const store = JSON.parse(sessionStorage.getItem(MATCH_STORE_KEY))
        return store && Array.isArray(store.matches) && store.matches.length ? store : null
    } catch (error) {
        return null
    }
}

const writeMatchStore = function (store) {
    try {
        if (store) {
            sessionStorage.setItem(MATCH_STORE_KEY, JSON.stringify(store))
        } else {
            sessionStorage.removeItem(MATCH_STORE_KEY)
        }
    } catch (error) {
        // Storage unavailable; the flow degrades to single-page decisions.
    }
}

const pushEvent = function (name) {
    window.dataLayer = window.dataLayer || []
    window.dataLayer.push({ event: name })
    if (typeof window.hfaDataLayer !== "undefined") {
        window.hfaDataLayer.push({ event: name })
    }
}

// =============================================================================
// The quiz page
// =============================================================================

const quiz = document.getElementById("ff-quiz")

if (quiz) {
    const slides = Array.from(quiz.querySelectorAll(".ff-slide"))
    const gateSlide = quiz.querySelector('[data-key="gate"]')
    const gateCalculating = gateSlide.querySelector(".ff-gate-calculating")
    const gateAuth = gateSlide.querySelector(".ff-gate-auth")

    let current = 0

    const state = {
        // Hard filters for the match scoring
        builderId: null,
        collection: null,
        // Every answer label keyed by question key; scored server-side
        answers: {},
        regionSlug: null,
        plansUrl: null,
        gateReached: false,
        fetching: false,
        done: false,
    }

    // =========================================================================
    // Slide navigation

    const goTo = function (index) {
        if (index < 0 || index >= slides.length) {
            return
        }
        slides[current].classList.remove("active")
        current = index
        const slide = slides[current]
        slide.classList.add("active")

        if (slide.dataset.key === "priorities") {
            updatePriorityAnswers()
        }
        if (slide.dataset.key === "gate") {
            runGate()
        }
    }

    // =========================================================================
    // Answer selection

    // The priorities question carries a personalized answer set per region
    // (data-region), mirroring the live quiz. Show the selected region's set,
    // falling back to the default set (data-region="") when the region has no
    // set of its own.
    const updatePriorityAnswers = function () {
        const prioritySlide = quiz.querySelector('[data-key="priorities"]')
        if (!prioritySlide) {
            return
        }
        const buttons = Array.from(prioritySlide.querySelectorAll(".ff-answer"))
        const hasRegionSet = buttons.some((btn) => btn.dataset.region === state.regionSlug)

        buttons.forEach((btn) => {
            const show = hasRegionSet
                ? btn.dataset.region === state.regionSlug
                : btn.dataset.region === ""
            btn.hidden = !show
        })
    }

    const clearSelection = function (slide) {
        slide.querySelectorAll(".ff-answer.selected").forEach((btn) => {
            btn.classList.remove("selected")
            btn.setAttribute("aria-pressed", "false")
        })
        if (slide.dataset.key === "priorities") {
            state.collection = null
        }
        delete state.answers[slide.dataset.key]
        const next = slide.querySelector(".ff-next")
        if (next) {
            next.disabled = true
        }
    }

    const selectAnswer = function (slide, btn) {
        const key = slide.dataset.key

        slide.querySelectorAll(".ff-answer").forEach((el) => {
            el.classList.toggle("selected", el === btn)
            el.setAttribute("aria-pressed", el === btn ? "true" : "false")
        })

        state.answers[key] = btn.textContent.trim()

        if (key === "region") {
            state.builderId = btn.dataset.value
            if (btn.dataset.region !== state.regionSlug) {
                state.regionSlug = btn.dataset.region
                state.plansUrl = btn.dataset.plansUrl || null
                // The region drives the priorities answer set; a stale
                // selection could reference another region's collection.
                const prioritySlide = quiz.querySelector('[data-key="priorities"]')
                if (prioritySlide) {
                    clearSelection(prioritySlide)
                }
            }
        }
        if (key === "priorities") {
            state.collection = btn.dataset.value
        }

        const next = slide.querySelector(".ff-next")
        if (next) {
            next.disabled = false
        }
    }

    // The visitor's inferred market (recent ViewContent activity or favorites,
    // resolved server-side in QuizPageView) arrives preselected.
    const preselected = quiz.querySelector('.ff-slide[data-key="region"] .ff-answer[data-preselected]')
    if (preselected) {
        selectAnswer(preselected.closest(".ff-slide"), preselected)
    }

    // GTM progress ping (dataLayer only): once per question, on the visitor's
    // first answer to it. Changing an answer, or a server-side preselection,
    // does not fire.
    const progressTracked = new Set()

    const trackProgress = function (slide) {
        const key = slide.dataset.key
        if (progressTracked.has(key)) {
            return
        }
        progressTracked.add(key)
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push({
            event: "ff-quiz-progress",
            question_num: String(slides.indexOf(slide) + 1).padStart(2, "0"),
        })
    }

    // =========================================================================
    // Gate: calculating pause, then sign-up gate (or straight to results)

    const runGate = function () {
        state.gateReached = true
        gateCalculating.hidden = false
        gateAuth.hidden = true

        setTimeout(function () {
            if (state.done) {
                return
            }
            if (authenticated()) {
                fetchMatches()
            } else {
                gateCalculating.hidden = true
                gateAuth.hidden = false
            }
        }, 1800)
    }

    // Continue past the gate once the retail user login/join form succeeds.
    // oilib's addSuccessAction is a single overridable hook that
    // inc/retail_user.js already assigns, so wrap (not replace) it.
    const chainFormSuccess = function (formId, action) {
        const wrap = function () {
            const form = window.oi && oi.loaded && oi.loaded.form && oi.loaded.form[formId]
            if (!form) {
                return false
            }
            const original = form.addSuccessAction
            form.addSuccessAction = function (resp) {
                if (typeof original === "function") {
                    original.call(form, resp)
                }
                action(resp)
            }
            return true
        }
        if (!wrap()) {
            // Not registered yet. retail_user.js assigns its success action in
            // this same event; the timeout wraps after it has run.
            window.addEventListener(`oi.loaded.form.${formId}`, function () {
                setTimeout(wrap, 0)
            })
        }
    }

    const onAuthSuccess = function () {
        if (!state.gateReached || state.done) {
            return
        }
        gateAuth.hidden = true
        gateCalculating.hidden = false
        fetchMatches()
    }

    chainFormSuccess("oi-join-form", onAuthSuccess)
    chainFormSuccess("oi-login-form", onAuthSuccess)

    // =========================================================================
    // Matches: score server-side, then step through them one at a time

    const fetchMatches = function () {
        // Both the gate timer and the login/join success hook call this; the
        // guard keeps a login completed mid-"calculating" from double-firing
        // the completion event (and double-saving the Lead note).
        if (state.fetching || state.done) {
            return
        }
        state.fetching = true

        // The quiz counts as completed only here: every question answered and
        // the visitor registered or logged in past the gate.
        pushEvent("oi-quiz-complete")

        const fallbackUrl = state.plansUrl || "/new-homes/floorplans/"
        const formData = new FormData()
        formData.append("answers", JSON.stringify(state.answers))
        if (state.builderId) {
            formData.append("b", state.builderId)
        }
        if (state.collection) {
            formData.append("plan_collection", state.collection)
        }

        oi.axios({
            method: "post",
            url: "/xhr/quiz-match/",
            data: formData,
        })
            .then(function (resp) {
                const matches = (resp.data.data && resp.data.data.matches) || []
                state.done = true

                if (!matches.length) {
                    window.location.assign(fallbackUrl)
                    return
                }

                writeMatchStore({ matches: matches, index: 0 })
                window.location.assign(matches[0].url)
            })
            .catch(function () {
                state.done = true
                window.location.assign(fallbackUrl)
            })
    }

    // =========================================================================
    // Wiring

    quiz.addEventListener("click", function (e) {
        const answer = e.target.closest(".ff-answer")
        if (answer) {
            const slide = answer.closest(".ff-slide")
            selectAnswer(slide, answer)
            trackProgress(slide)
            return
        }
        if (e.target.closest(".ff-prev")) {
            goTo(current - 1)
            return
        }
        const next = e.target.closest(".ff-next")
        if (next && !next.disabled) {
            goTo(current + 1)
        }
    })

    // Hide the "Already have an account?" prompt for logged-in users.
    window.addEventListener("load", function () {
        if (authenticated()) {
            quiz.querySelectorAll(".ff-auth-prompt").forEach(function (el) {
                el.hidden = true
            })
        }
    })
}

// =============================================================================
// The match decision pages (MasterPlanQuizView)
// =============================================================================

const decision = document.getElementById("ff-decision")

if (decision) {
    const hfid = decision.dataset.homefinitiId
    const loveBtn = decision.querySelector(".ff-love-it")
    const passBtn = decision.querySelector(".ff-no-thanks")
    const HOWTO_COOKIE = "ff-quiz-howto"

    const currentMatch = function () {
        const store = readMatchStore()
        if (!store) {
            return null
        }
        return store.matches.find(function (match) {
            return match.url === window.location.pathname
        }) || null
    }

    // Compatibility score: the weighted match score this plan earned when the
    // quiz ranked its top matches (carried in the queue), shown as a percent
    // inside the heart badge.
    const showMatchScore = function () {
        const match = currentMatch()
        const badge = decision.querySelector(".ff-match-score")
        if (!badge || !match || typeof match.score !== "number") {
            return
        }
        badge.querySelector(".ff-match-score-value").textContent = `${Math.round(match.score * 100)}%`
        badge.hidden = false
    }

    // First visit to a match page: explain the No Thanks / Love It! choices.
    // Seen and dismissed counts as read -- the 90-day cookie drops on ANY
    // dismissal (Start Falling, the X, or a backdrop click).
    const showHowTo = function () {
        if (!readMatchStore()) {
            return
        }
        const cookies = window.oi && oi.utils && oi.utils.cookie
        if (!cookies || cookies.get(HOWTO_COOKIE)) {
            return
        }
        const modalEl = document.getElementById("ff-howto-modal")
        if (!modalEl || !window.bootstrap) {
            return
        }
        modalEl.addEventListener(
            "hidden.bs.modal",
            function () {
                const cookie = cookies.set(HOWTO_COOKIE, "seen")
                cookie.expireDays(90)
                cookie.set()
            },
            { once: true }
        )
        window.bootstrap.Modal.getOrCreateInstance(modalEl).show()
    }

    showMatchScore()
    window.addEventListener("load", showHowTo)

    // Step to the next match in the queue; after the last one, land on the
    // dashboard's favorites so the visitor sees everything they loved.
    const advance = function () {
        const store = readMatchStore()
        if (!store) {
            return false
        }

        let index = store.matches.findIndex(function (match) {
            return match.url === window.location.pathname
        })
        if (index === -1) {
            index = store.index || 0
        }

        const next = store.matches[index + 1]
        if (next) {
            store.index = index + 1
            writeMatchStore(store)
            window.location.assign(next.url)
        } else {
            writeMatchStore(null)
            const slug = (window.oi_preload && window.oi_preload.dashboard_slug) || "my-portal"
            window.location.assign(`/${slug}/#favorites`)
        }
        return true
    }

    // Rage-click guard: on the first decision, freeze both buttons in place --
    // the clicked one swaps to the gate slide's spinner-border loading
    // graphic, the other fades out. The locked state persists until the next
    // match's page load replaces it. Returns false when already locked.
    const lockDecision = function (clicked) {
        if (decision.classList.contains("ff-deciding")) {
            return false
        }
        decision.classList.add("ff-deciding")

        const buttons = [passBtn, loveBtn].filter(Boolean)
        buttons.forEach(function (btn) {
            // Freeze the rendered footprint so the content swap and fade
            // can't shift positioning.
            btn.style.width = `${btn.offsetWidth}px`
            btn.style.height = `${btn.offsetHeight}px`
            btn.classList.add("disabled")
            btn.setAttribute("aria-disabled", "true")
            if (btn.tagName === "BUTTON") {
                btn.disabled = true
            }
            if (btn === clicked) {
                btn.innerHTML = '<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>'
            } else {
                btn.classList.add("ff-decision-fade")
            }
        })
        return true
    }

    // A bfcache restore (back button) would resurrect the locked state on a
    // page the visitor can interact with again; reload for a clean slate.
    window.addEventListener("pageshow", function (e) {
        if (e.persisted && decision.classList.contains("ff-deciding")) {
            window.location.reload()
        }
    })

    if (passBtn) {
        passBtn.addEventListener("click", function (e) {
            if (!readMatchStore()) {
                // Without a match queue the default href (the floorplan list) stands.
                return
            }
            e.preventDefault()
            if (!lockDecision(passBtn)) {
                return
            }
            advance()
        })
    }

    if (loveBtn) {
        loveBtn.addEventListener("click", function (e) {
            e.preventDefault()

            if (!authenticated()) {
                // Stage the favorite (oilib applies it right after login) and
                // open the login/join modal.
                if (window.oi && oi.state) {
                    oi.state.oiuser_staged = hfid
                }
                const modal = document.getElementById("oi-login-join-modal")
                if (modal && window.bootstrap) {
                    window.bootstrap.Modal.getOrCreateInstance(modal).show()
                }
                return
            }

            if (readMatchStore()) {
                if (!lockDecision(loveBtn)) {
                    return
                }
            } else if (loveBtn.disabled) {
                return
            }

            // Same effect as the heart button: force this plan INTO favorites
            // (never toggle it back out), then move on.
            oi.loaded.user
                .handle_toggle_request(hfid, true)
                .catch(function () {})
                .then(function () {
                    if (!advance()) {
                        // Direct visit outside the quiz flow: acknowledge the save.
                        loveBtn.innerHTML = '<i class="fa-solid fa-heart" aria-hidden="true"></i> Saved to Favorites'
                        loveBtn.disabled = true
                    }
                })
        })
    }
}
